import { Stringable } from './Stringable';
import collect from 'collect.js';
import { OrderData } from '../exports/Interfaces';

export type AddressFormat = 'us' | 'international' | 'number_city_zip' | 'number_zip_city';

interface Address {
  street_name?: string;
  street_number?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  note?: string;
  /**
   * The company's `region.address_format`, also for a delivery address across the border: the
   * payload carries no other region, and neighbouring countries mostly share a layout.
   * Absent only from a backend that predates it.
   */
  format?: AddressFormat;
}

export default class AddressHelpers {
  public static isDeliveryComplete(order: Partial<OrderData>): boolean {
    return Boolean(
      order?.delivery_street_name &&
      order?.delivery_street_number &&
      order?.delivery_city &&
      order?.delivery_zip_code &&
      order?.delivery_lat &&
      order?.delivery_lng,
    );
  }

  public static format({ street_name, street_number, city, state, zip_code, note, format }: Address): string {
    const addressFormat = format ?? 'international';

    // Two number-first shapes, named once and mapped onto every layout that uses them, instead
    // of repeating the implode() calls per bucket:
    //  - numberFirstWithState: "number street, note, city, state, zip" (US-style).
    //  - numberFirstWithPostcodeBeforeCity: "number street, zip city" (FR-style)
    // number_city_zip (GB/IE/ZA/JM/DO) is number-first with no state, city before zip — GB/IE
    // must be number-first: the continental "Birmingham Road 5" makes geocoders resolve
    // same-named streets kilometres away despite a correct postcode.
    // Everything else (international) falls through to the German "street name number, zip city".
    const address = {
      us: this.implode([this.implode([street_number, street_name], ' '), note, city, state, zip_code]),
      number_city_zip: this.implode([this.implode([street_number, street_name], ' '), city, zip_code]),
      number_zip_city: this.implode([
        this.implode([street_number, street_name], ' '),
        this.implode([zip_code, city], ' '),
      ]),
      international: this.implode([
        this.implode([street_name, street_number], ' '),
        this.implode([zip_code, city], ' '),
      ]),
    }[addressFormat];

    return new Stringable(address).squish().toString();
  }

  private static implode(fields: string[], glue: string = ', '): string {
    return collect(fields)
      .filter((item) => item && item.length > 0)
      .implode(glue)
      .trim();
  }
}
